import AbilityFuncs from '../models/game_pieces/AbilityFuncs';
import HeroBase from '../models/game_state/heroes';
import * as MobsBase from '../models/game_pieces/ai_units/mobs';
import SummonedUnitsBase from '../models/game_pieces/ai_units/summoned_units'; // TODO normalize to HeroBase
import getAbilityLevel from './getAbilityLevel';
import getEquipmentObjFromAssignments from './getEquipmentObjFromAssignments';
import { RosterHero } from '../../generated/zod';

const ALL_UNIT_MODELS = {
  ...HeroBase,
  ...MobsBase,
  ...SummonedUnitsBase,
};

const SECONDARY_BASE_ATTS = [
  'armorClass',
  'dodgeChance',
  'magicalGravity',
  'meleeDamage',
  'mentalResil',
  'physicalResil',
  'regeneration',
  'speed',
  'xp_bonus',
];

export default function getUnitState({
  ai_unit,
  roster_hero,
  unit_build,
  unit_conditions = {},
}: {
  ai_unit?: any; // TODO
  roster_hero?: RosterHero;
  unit_build?: any; // TODO
  unit_conditions?: any; // TODO
}) {
  const unit_handle = ai_unit?.handle ?? unit_build?.hero_handle ?? unit_build?.handle;
  let base_unit = ALL_UNIT_MODELS[unit_handle];
  if (!base_unit) {
    console.error({ ai_unit, unit_build });
    throw new Error(`no unit model found for unit_handle: "${unit_handle}"`);
  }

  if (ai_unit) {
    if (ai_unit.build) {
      unit_build = {
        ...ai_unit.build,
        ability_trees: ai_unit.ability_trees,
      };
    } else {
      unit_build = ai_unit;
    }
    unit_conditions = ai_unit.conditions || {};
  }
  const unit_bags = ai_unit?.bags ?? roster_hero?.bags;

  // initialize base attributes
  const atts = {
    base: ai_unit?.attributes.base ?? { ...base_unit.getBaseAttributes() },
    fromUpgrades: {
      learning: 0,
      perception: 0,
      mana: 0,
      toughness: 0,
      strength: 0,
      willpower: 0,
      dexterity: 0,
      ...(unit_build.attribute_upgrades || {}),
    },
  };
  for (const key of SECONDARY_BASE_ATTS) {
    if (!atts.base[key]) {
      atts.base[key] = 0;
    }
  }

  const attsTotal = {};

  // calculate attribute mods from unit conditions
  atts.fromConditions = {
    learning: 0,
    perception: 0,
    mana: 0,
    toughness: 0,
    strength: 0,
    willpower: 0,
    dexterity: 0,
    armorClass: 0,
  };
  for (const condition of Object.values(unit_conditions)) {
    if (condition?.handle === 'inspired') {
      atts.fromConditions.willpower += condition.potency;
    } else if (condition?.handle === 'armor_broken') {
      atts.fromConditions.armorClass -= condition.potency;
    }
  }

  atts.fromEquipment = {};
  const equipment = ai_unit?.equipment
    ? ai_unit.equipment
    : getEquipmentObjFromAssignments({ unit_bags, unit_build });

  for (const att_key of Object.keys(atts.base)) {
    // calculate equipment bonuses
    atts.fromEquipment[att_key] =
      (equipment.helmet && equipment.helmet[att_key] ? equipment.helmet[att_key] : 0) +
      (equipment.torsoe && equipment.torsoe[att_key] ? equipment.torsoe[att_key] : 0) +
      (equipment.boots && equipment.boots[att_key] ? equipment.boots[att_key] : 0) +
      (equipment.weapon && equipment.weapon[att_key] ? equipment.weapon[att_key] : 0) +
      (equipment.off_hand && equipment.off_hand[att_key] ? equipment.off_hand[att_key] : 0) +
      (equipment.left_ring && equipment.left_ring[att_key] ? equipment.left_ring[att_key] : 0) +
      (equipment.right_ring && equipment.right_ring[att_key] ? equipment.right_ring[att_key] : 0);

    // calculate the total
    attsTotal[att_key] = Math.max(
      1,
      atts.base[att_key] +
      atts.fromEquipment[att_key] +
      atts.fromUpgrades[att_key] +
      atts.fromConditions[att_key]
    );
  }

  attsTotal.mana *=
    1 +
    AbilityFuncs.mana_node.maBonus(
      getAbilityLevel({
        unit_build,
        tree_handle: 'conjuration',
        ability_handle: 'mana_node',
      })
    );

  // update secondary atts
  atts.regeneration = Math.min(
    35,
    attsTotal.strength / 8 +
    attsTotal.willpower / 10 +
    attsTotal.toughness / 15 +
    attsTotal.mana / 20 +
    atts.fromEquipment.regeneration
  );
  atts.regeneration = Math.max(1, atts.regeneration);

  atts.speed = Math.min(
    35,
    10.0 +
    attsTotal.dexterity / 8 +
    attsTotal.strength / 12 +
    attsTotal.willpower / 20 +
    attsTotal.mana / 50 +
    atts.fromEquipment.speed +
    AbilityFuncs.quickness.potency(
      getAbilityLevel({
        unit_build,
        tree_handle: 'movement',
        ability_handle: 'quickness',
      })
    )
  );
  atts.speed = Math.max(1, atts.speed);

  atts.magicalGravity = Math.min(
    35,
    attsTotal.mana / 5 +
    attsTotal.willpower / 14 +
    attsTotal.perception / 23 +
    atts.fromEquipment.magicalGravity +
    AbilityFuncs.mana_node.mgBonus(
      getAbilityLevel({
        unit_build,
        tree_handle: 'conjuration',
        ability_handle: 'mana_node',
      })
    )
  );
  atts.magicalGravity = Math.max(1, atts.magicalGravity);

  attsTotal.meleeDamage = atts.meleeDamage =
    50 +
    attsTotal.strength * 2 +
    attsTotal.dexterity +
    attsTotal.learning +
    (atts.fromEquipment.meleeDamage || 0);

  atts.dodgeChance = Math.min(
    65,
    attsTotal.perception / 4 +
    attsTotal.dexterity / 7 +
    attsTotal.mana / 16 +
    AbilityFuncs.blurred_form.potency(
      getAbilityLevel({
        unit_build,
        tree_handle: 'illusion',
        ability_handle: 'blurred_form',
      })
    ) *
    100 +
    (atts.fromEquipment.dodgeChance || 0)
  );

  atts.physicalResil = Math.min(
    65,
    attsTotal.strength / 5 +
    attsTotal.dexterity / 5 +
    AbilityFuncs.freedom.potency(
      getAbilityLevel({
        unit_build,
        tree_handle: 'movement',
        ability_handle: 'freedom',
      })
    ) *
    100 +
    AbilityFuncs.blurred_form.potency(
      getAbilityLevel({
        unit_build,
        tree_handle: 'illusion',
        ability_handle: 'blurred_form',
      })
    ) *
    100 +
    (atts.fromEquipment.physicalResil || 0)
  );

  atts.mentalResil = Math.min(
    65,
    attsTotal.learning / 5 +
    attsTotal.willpower / 5 +
    AbilityFuncs.iron_will.potency(
      getAbilityLevel({
        unit_build,
        tree_handle: 'spirit',
        ability_handle: 'iron_will',
      })
    ) *
    100 +
    AbilityFuncs.blurred_form.potency(
      getAbilityLevel({
        unit_build,
        tree_handle: 'illusion',
        ability_handle: 'blurred_form',
      })
    ) *
    100 +
    (atts.fromEquipment.mentalResil || 0)
  );

  atts.armorClass =
    atts.fromEquipment.armorClass +
    atts.fromConditions.armorClass +
    AbilityFuncs.layers_of_protection.potency(
      getAbilityLevel({
        unit_build,
        tree_handle: 'armor_use',
        ability_handle: 'layers_of_protection',
      })
    );
  atts.armorClass = Math.max(0, atts.armorClass);

  // calculate max stats (hp, ap, mp)
  const max_hp = Math.round(
    attsTotal.toughness * 25 +
    attsTotal.strength * 15 +
    attsTotal.willpower * 10 +
    attsTotal.mana * 5
  );
  const max_ap =
    100 +
    2 *
    getAbilityLevel({
      unit_build,
      tree_handle: 'movement',
      ability_handle: 'preparation',
    });
  const max_mp = Math.round(
    attsTotal.mana * 10 + attsTotal.learning * 7 + attsTotal.willpower * 4 + attsTotal.toughness * 2
  );

  const result_unit_state = {
    ...unit_build,
    attributes: atts,
    conditions: unit_conditions,
    equipment,
    level: ai_unit?.level ?? roster_hero.level,
    max_hp,
    max_ap,
    max_mp,
  };

  // calculate ability params
  let ability_trees = ai_unit?.ability_trees ?? base_unit.getAbilityTrees();
  if (!Array.isArray(ability_trees) && typeof ability_trees === 'object') {
    ability_trees = Object.values(ability_trees);
  }
  // clone ability_trees to avoid mutating the original
  const cloned_ability_trees = JSON.parse(JSON.stringify(ability_trees));
  for (const ability_tree of cloned_ability_trees) {
    for (const [ability_handle, ability] of Object.entries(ability_tree.abilities)) {
      const level = getAbilityLevel({
        ability_handle,
        tree_handle: ability_tree.handle,
        unit_build,
      });

      if (AbilityFuncs[ability_handle].range) {
        ability.range = AbilityFuncs[ability_handle].range(level, attsTotal, result_unit_state);
      } else {
        delete ability['range'];
      }

      if (AbilityFuncs[ability_handle].hpCost) {
        ability.hpCost = AbilityFuncs[ability_handle].hpCost(level, attsTotal, result_unit_state);
      } else {
        delete ability['hpCost'];
      }

      if (AbilityFuncs[ability_handle].apCost) {
        ability.apCost = AbilityFuncs[ability_handle].apCost(level, attsTotal, result_unit_state);
      } else {
        delete ability['apCost'];
      }

      if (AbilityFuncs[ability_handle].mpCost) {
        ability.mpCost = AbilityFuncs[ability_handle].mpCost(level, attsTotal, result_unit_state);
      } else {
        delete ability['mpCost'];
      }

      if (AbilityFuncs[ability_handle].charges) {
        ability.charges = AbilityFuncs[ability_handle].charges(level, attsTotal, result_unit_state);
      } else {
        delete ability['charges'];
      }

      if (AbilityFuncs[ability_handle].damage) {
        ability.damage = AbilityFuncs[ability_handle].damage(level, attsTotal, result_unit_state);
      } else {
        delete ability['damage'];
      }

      if (AbilityFuncs[ability_handle].healing) {
        ability.healing = AbilityFuncs[ability_handle].healing(level, attsTotal, result_unit_state);
      } else {
        delete ability['healing'];
      }

      if (AbilityFuncs[ability_handle].potency) {
        ability.potency = AbilityFuncs[ability_handle].potency(level, attsTotal, result_unit_state);
      } else {
        delete ability['potency'];
      }

      if (AbilityFuncs[ability_handle].duration) {
        ability.duration = AbilityFuncs[ability_handle].duration(
          level,
          attsTotal,
          result_unit_state
        );
      } else {
        delete ability['duration'];
      }

      if (AbilityFuncs[ability_handle].effectRange) {
        ability.effectRange = AbilityFuncs[ability_handle].effectRange(
          level,
          attsTotal,
          result_unit_state
        );
      } else {
        delete ability['effectRange'];
      }

      if (AbilityFuncs[ability_handle].maBonus) {
        ability.maBonus = AbilityFuncs[ability_handle].maBonus(level, attsTotal, result_unit_state);
      } else {
        delete ability['maBonus'];
      }

      if (AbilityFuncs[ability_handle].mgBonus) {
        ability.mgBonus = AbilityFuncs[ability_handle].mgBonus(level, attsTotal, result_unit_state);
      } else {
        delete ability['mgBonus'];
      }
    }
  }
  result_unit_state.abilityTrees = cloned_ability_trees;

  return result_unit_state;
}
